import _ from "lodash";
import moment from "moment-timezone";

export const liveAnalyticsMixin = {
  computed: {
    allDepartments(this: any) {
      return _.get(this.$store, "state.department.departments", []);
    },
    userDepartments(this: any) {
      return _.get(this.$store, "state.profile.app_metadata.departments", []);
    },

    customDepartmentFilterSettings(this: any) {
      const liveChatLiveAnalytics =
        this.$store.state.modules?.handover?.liveChatLiveAnalytics || {};

      return {
        enabled: liveChatLiveAnalytics.enableCustomDepartment,
        filters: liveChatLiveAnalytics.customDepartmentFilter?.departments || [],
      };
    },
    isCustomDepartmentFilterEnabled(this: any) {
      return this.customDepartmentFilterSettings.enabled;
    },
    departments(this: any) {
      const allDepartments = this.allDepartments;

      if (this.isCustomDepartmentFilterEnabled) {
        const customFilters = this.customDepartmentFilterSettings.filters;

        return _.compact(
          customFilters.map((filter) => {
            const depts: string[] = [];

            if (filter.departments && Array.isArray(filter.departments)) {
              filter.departments.forEach((dept) => {
                if (!this.isWithinUserDepartments(dept)) return;

                const deptObj = allDepartments.find(
                  (d: any) => d.name?.toLowerCase() === dept.toLowerCase()
                );

                if (deptObj) {
                  depts.push(deptObj.id);
                }
              });
            }

            if (depts.length === 0) {
              return null;
            }

            return {
              name: filter.name,
              value: depts,
            };
          })
        );
      }

      return _.compact(
        allDepartments.map((dept) => {
          if (!this.isWithinUserDepartments(dept.name)) return null;

          return {
            name: dept.name,
            value: [dept.id],
          };
        })
      );
    },
  },
  methods: {
    isWithinUserDepartments(this: any, deptName: string) {
      return _.map(this.userDepartments, _.lowerCase).some(
        (value) => value === deptName.toLowerCase()
      );
    },

    convertDepartmentIdsToNames(this: any, departmentIds: string[]) {
      const allDepartments = this.allDepartments;

      return departmentIds.map((deptId) => {
        const dept = allDepartments.find((d: any) => d.id === deptId);
        return dept?.name || deptId;
      });
    },

    agentMatchesDepartmentFilter(agent, selectedDepartments) {
      const agentDepartments = this.parseAgentDepartments(agent);

      return agentDepartments.some((agentDept) => {
        const agentDeptValue = agentDept?.name || agentDept;

        // If agentDeptValue is a string, convert to lowercase for comparison
        const agentDeptLower =
          typeof agentDeptValue === "string" ? agentDeptValue.toLowerCase() : "";

        // Check against lowercase names (for agents with dept as names)
        if (selectedDepartments.includes(agentDeptLower)) {
          return true;
        }

        // Check against IDs directly (for agents with dept as IDs or for when selectedDeps contains IDs)
        if (selectedDepartments.includes(agentDeptValue)) {
          return true;
        }

        return false;
      });
    },

    // Parse agent departments from JSON string or array
    parseAgentDepartments(agent: any) {
      let departments = agent.departments || [];
      if (typeof departments === "string") {
        try {
          departments = JSON.parse(departments);
        } catch {
          departments = [];
        }
      }
      return departments;
    },

    // Get display-friendly department name from a raw dept value (ID or name string)
    getDepartmentLabel(this: any, rawDept: string) {
      if (!rawDept) return null;

      const allDepartments = this.allDepartments;

      const deptById = allDepartments.find((d: any) => d.id === rawDept);

      if (deptById) return deptById.name;

      const deptByName = allDepartments.find(
        (d: any) => d.name?.toLowerCase() === rawDept?.toLowerCase()
      );

      if (deptByName) return deptByName.name;

      return null;
    },

    getRawDepartmentFromChat(chat: any) {
      return chat.stateVariables?.userQueryTopic || chat.department || null;
    },

    getChatDepartmentId(this: any, chat: any) {
      const rawDept = this.getRawDepartmentFromChat(chat);

      if (!rawDept) return null;

      const allDepartments = this.allDepartments;

      const deptById = allDepartments.find((d: any) => d.id === rawDept);

      if (deptById) return deptById.id;

      const deptByName = allDepartments.find(
        (d: any) => d.name?.toLowerCase() === rawDept?.toLowerCase()
      );

      if (deptByName) return deptByName.id;

      return rawDept;
    },

    getCountForDepartments(
      type: string,
      selectedDeptIds: string[],
      departmentIndex: Record<string, any>
    ) {
      if (!selectedDeptIds?.length) return 0;

      const allChatIds = new Set<string>();

      // Count chats in the department index (by ID)
      for (const deptId of selectedDeptIds) {
        const deptIndex = departmentIndex[deptId];
        if (deptIndex && deptIndex[type]) {
          deptIndex[type].forEach((id: string) => allChatIds.add(id));
        }
      }

      return allChatIds.size;
    },

    buildDepartmentIndex(this: any, chatsByType: Record<string, any[]>) {
      const index: Record<string, Record<string, Set<string>>> = {};

      Object.entries(chatsByType).forEach(([type, chats]) => {
        chats.forEach((chat: any) => {
          const chatId = chat.RowKey || chat.id;
          const department = this.getChatDepartmentId(chat);

          if (department) {
            if (!index[department]) {
              index[department] = {
                resolved: new Set(),
                abandoned: new Set(),
                inProgress: new Set(),
                queue: new Set(),
              };
            }
            index[department][type].add(chatId);
          }
        });
      });

      return index;
    },

    formatDuration(ms: number) {
      const duration = moment.duration(ms);
      const totalMinutes = Math.floor(duration.asMinutes());
      const seconds = duration.seconds();

      const mm = String(totalMinutes).padStart(2, "0");
      const ss = String(seconds).padStart(2, "0");

      return `${mm}:${ss}`;
    },

    formatDurationHuman(ms: number) {
      const duration = moment.duration(ms);
      const hours = Math.floor(duration.asHours());
      const minutes = duration.minutes();
      const seconds = duration.seconds();

      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      }
      if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
      }
      return `${seconds}s`;
    },

    getChatId(chat: any) {
      return chat?.chatId || chat?.RowKey || chat?.id || null;
    },
  },
};
